import React from 'react';

import { ActionsMenu, Button, Icon } from '@neptune/shared/venus-ui';

import { ColorPicker } from './ColorPicker';
import { useColorPicker } from './useColorPicker';

type ColorPickerMenuBaseProps = {
  label?: string;
  color?: string;
  className?: string;
  presetColors?: string[];
  dataRole?: string;
  onAddPresetColor?: (color: string) => void;
};

type ColorPickerMenuClearableProps = ColorPickerMenuBaseProps & {
  clearable: true;
  onChange?: (color: string | undefined) => void;
};

type ColorPickerMenuStandardProps = ColorPickerMenuBaseProps & {
  clearable?: false;
  onChange?: (color: string) => void;
};

type ColorPickerMenuProps = ColorPickerMenuStandardProps | ColorPickerMenuClearableProps;

export function ColorPickerMenu(props: ColorPickerMenuStandardProps): JSX.Element;
export function ColorPickerMenu(props: ColorPickerMenuClearableProps): JSX.Element;

export function ColorPickerMenu({
  className,
  color,
  presetColors,
  onChange,
  clearable,
  onAddPresetColor,
  label,
  dataRole,
}: ColorPickerMenuProps): JSX.Element {
  const [isOpen, setIsOpen] = React.useState(false);

  const { handleExpand, handleCollapse, restoreColor, handleOnChange } = useColorPicker({
    color,
    onAddPresetColor,
    onChange,
    clearable,
  } as ColorPickerMenuProps);

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      setIsOpen(open);

      if (!open) {
        handleCollapse();
      } else {
        handleExpand();
      }
    },
    [handleCollapse, handleExpand],
  );

  return (
    <ActionsMenu.Sub open={isOpen} onOpenChange={handleOpenChange}>
      <ActionsMenu.SubTrigger asChild>
        <ActionsMenu.SubMenuItem leadingIcon={<Icon glyph="color" />} data-role={dataRole}>
          {label}
        </ActionsMenu.SubMenuItem>
      </ActionsMenu.SubTrigger>
      <ActionsMenu.Portal>
        <ActionsMenu.SubContent
          data-role="color-picker-menu-content"
          footer={
            clearable &&
            onChange && (
              <>
                <ActionsMenu.Footer justifyContent="end">
                  <Button
                    variant="transparent"
                    onClick={() => {
                      onChange(undefined);
                      setIsOpen(false);
                    }}
                  >
                    Reset to defaults
                  </Button>
                </ActionsMenu.Footer>
              </>
            )
          }
        >
          <ColorPicker
            className={className}
            defaultValue={color}
            onChange={handleOnChange}
            onCancel={restoreColor}
            presetColors={presetColors}
          />
        </ActionsMenu.SubContent>
      </ActionsMenu.Portal>
    </ActionsMenu.Sub>
  );
}
